import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const withTimestamp = (base, extension) =>
  `${base}_${new Date().toISOString()}.${extension}`;

const triggerDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const createExcelBlob = (rows, sheetName) => {
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { type: 'application/octet-stream' });
};

export const exportAsExcel = ({ rows, sheetName, fileNameBase = 'export' }) => {
  const blob = createExcelBlob(rows, sheetName);
  triggerDownload(blob, withTimestamp(fileNameBase, 'xlsx'));
};

export const downloadExcelFile = ({ rows, sheetName, fileName }) => {
  const blob = createExcelBlob(rows, sheetName);
  triggerDownload(blob, fileName);
};

export const exportAsCSV = ({ rows, fileNameBase = 'export' }) => {
  const headers = Object.keys(rows[0] || {});
  const csvRows = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((header) => {
          const cell = row[header] ?? '';
          const value = String(cell).replace(/"/g, '""');
          return `"${value}"`;
        })
        .join(',')
    ),
  ];
  const blob = new Blob(['\ufeff' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, withTimestamp(fileNameBase, 'csv'));
};

/**
 * Standard PDF export — produces a consistently branded PDF for every section of the app.
 *
 * @param {Object[]} rows     - Array of data rows (objects or arrays)
 * @param {string[]} headers  - Column header labels (optional, inferred from keys if omitted)
 * @param {string}   title    - Report title shown on the PDF
 * @param {string}   fileNameBase - Base filename (date appended automatically)
 * @param {string}   orientation  - 'l' (landscape) or 'p' (portrait), default 'l'
 */
export const exportAsPDF = ({
  rows,
  headers,
  title = 'Report',
  fileNameBase = 'export',
  orientation = 'l',
  columnStyles = {}, // Added optional column styling support
}) => {
  const BRAND_COLOR = [83, 109, 254];
  const DARK_TEXT   = [33, 33, 33];
  const LIGHT_GREY  = [248, 249, 250];
  const MID_GREY    = [204, 204, 204];

  const pdfHeaders = headers || (rows.length > 0 ? Object.keys(rows[0]) : []);
  const colCount   = pdfHeaders.length;

  // Auto-scale styles based on column count to prevent overlapping
  const fontSize   = colCount > 14 ? 6 : colCount > 10 ? 7 : 8;
  const cellPad    = colCount > 14 ? 2 : colCount > 10 ? 3 : 5;
  const headFontSz = fontSize + 1;

  const doc = new jsPDF(orientation, 'pt', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // ── Header ────────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...DARK_TEXT);
  doc.text(title, 40, 46);

  doc.setDrawColor(...BRAND_COLOR);
  doc.setLineWidth(1.5);
  doc.line(40, 56, pageW - 40, 56);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, 40, 73);
  doc.text(`Total Records: ${rows.length}`, pageW - 40, 73, { align: 'right' });

  // ── Table ──────────────────────────────────────────────────────────────────
  autoTable(doc, {
    startY: 88,
    head: [pdfHeaders],
    body: rows.map((row) => {
      if (Array.isArray(row)) {
        return row.map(v => (v === null || v === undefined ? '' : String(v).replace(/₹/g, 'Rs. ')));
      }
      return pdfHeaders.map((h) => {
        const v = row[h];
        // Replace Rupee symbol with 'Rs.' as default jsPDF fonts don't support it correctly
        return v === null || v === undefined ? '' : String(v).replace(/₹/g, 'Rs. ');
      });
    }),
    styles: {
      fontSize,
      overflow: 'linebreak',
      cellPadding: cellPad,
      lineColor: MID_GREY,
      lineWidth: 0.2,
      textColor: DARK_TEXT,
      cellWidth: 'wrap',
    },
    headStyles: {
      fillColor: BRAND_COLOR,
      textColor: [255, 255, 255],
      fontSize: headFontSz,
      fontStyle: 'bold',
      halign: 'left',
    },
    columnStyles, // Inject dynamic column styling
    alternateRowStyles: { fillColor: LIGHT_GREY },
    tableWidth: 'auto',
    margin: { top: 50, right: 40, bottom: 55, left: 40 },

    // ── Footer on every page ─────────────────────────────────────────────────
    didDrawPage: () => {
      const totalPages = doc.internal.getNumberOfPages();
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;

      doc.setDrawColor(...MID_GREY);
      doc.setLineWidth(0.5);
      doc.line(40, pageH - 42, pageW - 40, pageH - 42);

      doc.setFontSize(8);
      doc.setTextColor(130, 130, 130);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${currentPage} of ${totalPages}`, pageW - 40, pageH - 26, { align: 'right' });
    },
  });

  doc.save(`${fileNameBase}_${new Date().toISOString().split('T')[0]}.pdf`);
};


export const readExcelFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const arrayBuffer = event.target?.result;
        if (!arrayBuffer) {
          reject(new Error('Unable to read file contents.'));
          return;
        }
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read the selected file.'));
    reader.readAsArrayBuffer(file);
  });

